import { request, requestWithMeta } from './client.js'

export interface Region {
  readonly id: string
  readonly code: string
  readonly name: string
}

export interface Tuk {
  readonly id: string
  readonly code: string
  readonly name: string
  readonly address: string | null
  readonly isVerified: boolean
  readonly region: { readonly code: string; readonly name: string }
  readonly _count: { readonly rooms: number; readonly inventory: number }
}

export interface Room {
  readonly id: string
  readonly tukId: string
  readonly code: string
  readonly name: string
  readonly capacity: number
}

export const INVENTORY_CATEGORIES = ['MANEKIN', 'ALAT', 'BAHAN_HABIS_PAKAI', 'FURNITUR'] as const
export type InventoryCategory = (typeof INVENTORY_CATEGORIES)[number]

export interface InventoryItem {
  readonly id: string
  readonly tukId: string
  readonly category: InventoryCategory
  readonly name: string
  readonly quantity: number
  readonly condition: string
}

export async function listRegions(): Promise<readonly Region[]> {
  const { data } = await requestWithMeta<readonly Region[]>('/api/v1/region?limit=100')
  return data
}

export async function listTuk(
  page: number,
  q: string,
): Promise<{ rows: readonly Tuk[]; total: number }> {
  const params = new URLSearchParams({ page: String(page), limit: '25' })
  if (q !== '') params.set('q', q)
  const { data, total } = await requestWithMeta<readonly Tuk[]>(`/api/v1/tuk?${params}`)
  return { rows: data, total }
}

export async function createTuk(input: {
  code: string
  name: string
  regionId: string
  address?: string
}): Promise<Tuk> {
  return request<Tuk>('/api/v1/tuk', { method: 'POST', body: input })
}

export async function setVerified(id: string, isVerified: boolean): Promise<Tuk> {
  return request<Tuk>(`/api/v1/tuk/${id}/verifikasi`, { method: 'POST', body: { isVerified } })
}

export async function listRooms(tukId: string): Promise<readonly Room[]> {
  const { data } = await requestWithMeta<readonly Room[]>(`/api/v1/ruang?tukId=${tukId}&limit=100`)
  return data
}

export async function createRoom(input: {
  tukId: string
  code: string
  name: string
  capacity?: number
}): Promise<Room> {
  return request<Room>('/api/v1/ruang', { method: 'POST', body: input })
}

export async function deleteRoom(id: string): Promise<unknown> {
  return request<unknown>(`/api/v1/ruang/${id}`, { method: 'DELETE' })
}

export async function listInventory(tukId: string): Promise<readonly InventoryItem[]> {
  const { data } = await requestWithMeta<readonly InventoryItem[]>(
    `/api/v1/inventaris?tukId=${tukId}&limit=100`,
  )
  return data
}

export async function createInventory(input: {
  tukId: string
  category: InventoryCategory
  name: string
  quantity?: number
  condition?: string
}): Promise<InventoryItem> {
  return request<InventoryItem>('/api/v1/inventaris', { method: 'POST', body: input })
}

export async function deleteInventory(id: string): Promise<unknown> {
  return request<unknown>(`/api/v1/inventaris/${id}`, { method: 'DELETE' })
}
